import { DateTime } from 'luxon'

import { Account } from './account/account'
import { Role } from './role/role'
import { ResellerSettings } from './user/resellerSettings'

export class User {
	public id: number
	public accountId: number
	public uuid: string
	public email: string
	public name: string | null
	public firstname: string
	public lastname: string
	public phoneNumber: string | null
	public roleId: number
	public role: string
	public country: string | null
	public status: string
	public createdAt: DateTime
	public availableSince: DateTime
	public updatedAt: DateTime
	public disabledAt: DateTime | null
	public disabled: boolean
	public suspended: boolean
	public test: boolean
	public avatar: string | null
	public lastSeen: DateTime | null
	public comments: string | null
	public flags: string[]
	public permissions?: string[]
	public posthogId: string | null
	public isResellerSubaccount?: boolean
	public resellerSettings?: ResellerSettings
	public roles?: {
		data: Role[]
	}
	public account?: Account
	public childrenAccounts?: Account[]

	constructor(data: {
		id: number
		accountId: number
		uuid: string
		email: string
		name: string | null
		firstname: string
		lastname: string
		phoneNumber: string | null
		roleId: number
		role: string
		roles?: {
			data: Role[]
		}
		country: string | null
		status: string
		createdAt: DateTime
		availableSince: DateTime
		updatedAt: DateTime
		disabledAt: DateTime | null
		disabled: boolean
		suspended: boolean
		test: boolean
		avatar: string | null
		lastSeen: DateTime | null
		comments: string | null
		flags: string[]
		permissions?: string[]
		isResellerSubaccount?: boolean
		resellerSettings?: ResellerSettings
		account?: Account
		childrenAccounts?: Account[]
		posthogId: string | null
	}) {
		this.id = data.id
		this.accountId = data.accountId
		this.uuid = data.uuid
		this.email = data.email
		this.name = data.name
		this.firstname = data.firstname
		this.lastname = data.lastname
		this.phoneNumber = data.phoneNumber
		this.roleId = data.roleId
		this.role = data.role
		this.country = data.country
		this.status = data.status
		this.createdAt = data.createdAt
		this.availableSince = data.availableSince
		this.updatedAt = data.updatedAt
		this.disabledAt = data.disabledAt
		this.disabled = data.disabled
		this.suspended = data.suspended
		this.test = data.test
		this.avatar = data.avatar
		this.lastSeen = data.lastSeen
		this.comments = data.comments
		this.flags = data.flags
		this.permissions = data.permissions
		this.posthogId = data.posthogId
		this.isResellerSubaccount = data.isResellerSubaccount
		this.resellerSettings = data.resellerSettings
		this.account = data.account
		this.childrenAccounts = data.childrenAccounts
		this.roles = data.roles
	}

	// Factory method for creating from partial data (safer than using Object.assign directly)
	static create(partial: Partial<User>): User {
		// Set sensible defaults for missing properties
		const defaults = {
			id: 0,
			accountId: 0,
			uuid: '',
			email: '',
			name: null,
			firstname: '',
			lastname: '',
			phoneNumber: null,
			roleId: 0,
			role: '',
			country: null,
			status: '',
			createdAt: DateTime.now(),
			availableSince: DateTime.now(),
			updatedAt: DateTime.now(),
			disabledAt: null,
			disabled: false,
			suspended: false,
			test: false,
			avatar: null,
			lastSeen: null,
			comments: null,
			posthogId: null,
			flags: [],
		}

		// Merge defaults with provided data
		return new User({ ...defaults, ...partial })
	}

	/**
	 * Get the full name of the user. It will use the available user data to build the full name.
	 * If the user has a first name and last name, it will use that.
	 * If the user has a name, it will use that.
	 * If the user has an email, it will use that.
	 */
	getFullName(): string {
		const first = this.firstname || ''
		const last = this.lastname || ''

		if (first || last) {
			return `${first} ${last}`.trim()
		}

		return this.name || this.email || ''
	}
}
